import gfcfThemeConfig from 'gfcf-theme-config';
import { animate, consoleInfo } from '@gravityforms/utils';
import elements from './elements';
import state from './state';

// set defaults for animation settings here if not found in config.
const settings = gfcfThemeConfig?.data?.animation_settings || {
	fields: {
		delay: 25,
		distance_from: 'calc(-50% + 20px)',
		distance_to: '-50%',
		duration: 400,
		easing: 'ease-in-out',
		in_type: 'fadeIn translateY',
		out_type: 'fadeOut',
	},
	logoNavBar: {
		distance_from: 0,
		distance_to: 0,
		duration: 300,
		in_type: 'fadeIn',
	},
	welcome: {
		in_distance_from: '-30px',
		in_distance_to: '0px',
		in_duration: 300,
		in_type: 'fadeIn translateY',
		out_distance_to: '-100px',
		out_duration: 600,
		out_type: 'fadeOut translateY',
	},
	enabled: true,
};

// destructure settings to make them easier to use and utilize camelCase.
const {
	fields: {
		delay,
		distance_from: distanceFrom,
		distance_to: distanceTo,
		duration,
		easing,
		in_type: inType,
		out_type: outType,
	},
	logoNavBar: {
		distance_from: logoNavBarDistanceFrom,
		distance_to: logoNavBarDistanceTo,
		duration: logoNavBarDuration,
		in_type: logoNavBarInType,
	},
	welcome: {
		in_distance_from: welcomeInDistanceFrom,
		in_distance_to: welcomeInDistanceTo,
		in_duration: welcomeInDuration,
		in_type: welcomeInType,
		out_distance_to: welcomeOutDistanceTo,
		out_duration: welcomeOutDuration,
		out_type: welcomeOutType,
	},
	enabled,
} = settings;

// destructure elements to make them easier to use.
const { conversationalForm, logo, formNav, formNavButtons, root, welcomeScreen, welcomeScreenElements } = elements;

// set up main animation options for fields animating in.
const fieldIn = {
	delay,
	distanceFrom,
	distanceTo,
	duration,
	easing,
	types: inType,
};

// set up main animation options for fields animating out.
const fieldOut = {
	delay,
	duration,
	easing,
	types: outType,
};

// options for animating in the welcome screen
const welcomeIn = {
	distanceFrom: welcomeInDistanceFrom,
	distanceTo: welcomeInDistanceTo,
	duration: welcomeInDuration,
	easing,
	types: welcomeInType,
};

const logoNavBarIn = {
	distanceFrom: logoNavBarDistanceFrom,
	distanceTo: logoNavBarDistanceTo,
	duration: logoNavBarDuration,
	easing,
	types: logoNavBarInType,
};

/**
 * @param  event
 * @function handleNavigateWelcome
 * @description Animate the welcome screen out and reveal the first active field.
 *
 * @since 1.0.0
 *
 * @return {void}
 */
const handleNavigateWelcome = ( event ) => {
	const { detail: { welcome, form } } = event;
	animate.run( welcomeScreen, {
		onAnimateStart: () => {
			welcome.onAnimateStart();
			formNavButtons.style.opacity = '0';
			root.classList.remove( 'gform-conversational--welcome-active' );
			animate.run( formNavButtons, {
				types: 'fadeIn',
				delay: welcomeOutDuration,
				duration,
				easing,
			} );
		},
		onAnimateEnd: () => {
			welcomeScreen.style.display = 'none';
		},
		distanceTo: welcomeOutDistanceTo,
		distanceFrom: '0px',
		duration: welcomeOutDuration,
		easing,
		types: welcomeOutType,
	} );
	animate.run( conversationalForm, {
		onAnimateStart: form.onAnimateStart,
		onAnimateEnd: () => {
			animate.run( form.firstField, fieldIn );
		},
		delay: parseInt( welcomeOutDuration, 10 ) / 4,
		duration,
		easing,
		types: 'fadeIn',
	} );
};

/**
 * @function handleNavigateStart
 * @description Fired at the beginning of navigation to either a next or previous field. Fades out the current field.
 *
 * @since 1.0.0
 *
 * @param {CustomEvent} e The event object for gfcf/conversational/navigate/start.
 *
 * @return {void}
 */
const handleNavigateStart = ( e ) => {
	const { detail: { target } } = e;
	animate.run( target, fieldOut );
};

/**
 * @function handleNavigateNext
 * @description Fired after field validation is successful and animates in the next field.
 *
 * @since 1.0.0
 *
 * @param {CustomEvent} e The event object for gfcf/conversational/navigate/next.
 *
 * @return {void}
 */
const handleNavigateNext = ( e ) => {
	state.animating = true;
	const { detail: { target } } = e;
	animate.run( target, fieldIn );
};

/**
 * @function handleNavigatePrev
 * @description Animates to the previous field.
 *
 * @since 1.0.0
 *
 * @param {CustomEvent} e The event object for gfcf/conversational/navigate/prev.
 *
 * @return {void}
 */
const handleNavigatePrev = ( e ) => {
	const { detail: { target } } = e;
	animate.run( target, fieldIn );
};

/**
 * @function handleNavigateError
 * @description If a validation error occurs, animate the current field back in.
 *
 * @since 1.0.0
 *
 * @param {CustomEvent} e The event object for gfcf/conversational/navigate/error.
 *
 * @return {void}
 */
const handleNavigateError = ( e ) => {
	const { detail: { target } } = e;
	animate.run( target, fieldIn );
};

/**
 * @function revealWelcomeElements
 * @description Reveals the welcome screen elements by animating them in in a staggered way.
 *
 * @since 1.0.0
 *
 * @return {void}
 */
const revealWelcomeElements = () => {
	if ( logo ) {
		welcomeScreenElements.unshift( logo );
	}
	if ( ! welcomeScreenElements.length ) {
		return;
	}

	const animationDelay = Number( welcomeInDuration ) / 3.5;
	let stagger = 0;

	animate.run( formNav, {
		delay: animationDelay,
		...logoNavBarIn,
	} );

	animate.runGroup( welcomeScreenElements.map( ( target ) => {
		stagger = stagger + animationDelay;
		const animationOptions = target.dataset.js === 'gform-conversational-logo' ? logoNavBarIn : welcomeIn;
		const options = {
			delay: stagger,
			...animationOptions,
		};
		return { target, options };
	} ) );
};

/**
 * @function bindEvents
 * @description Bind event listeners for this module.
 *
 * @since 1.0.0
 *
 * @return {void}
 */
const bindEvents = () => {
	document.addEventListener( 'gfcf/conversational/navigate/error', handleNavigateError );
	document.addEventListener( 'gfcf/conversational/navigate/next', handleNavigateNext );
	document.addEventListener( 'gfcf/conversational/navigate/prev', handleNavigatePrev );
	document.addEventListener( 'gfcf/conversational/navigate/welcome', handleNavigateWelcome );
	document.addEventListener( 'gfcf/conversational/navigate/start', handleNavigateStart );
};

/**
 * @function init
 * @description Kick off this modules functionality
 *
 */
const init = () => {
	if ( ! enabled ) {
		return;
	}
	bindEvents();
	revealWelcomeElements();

	consoleInfo( 'Gravity Forms Conversational Forms: Initialized form animations.' );
};

export default init;
